/**
 * Settings for the 🪝 wick-rejection candle marker (utils/wickRejection.ts):
 * - WickRejectionSettingsRow: under the Levels menu toggle (Markers tab);
 * - WickRejectionSettingsPanel: the Settings > Strategy "Wick rejection 🪝"
 *   tab (on/off + the same fields).
 * Both write straight to the persisted stores (levelsStore `wickRejection`,
 * wickRejectionSettingsStore), so they apply at once and stay in sync.
 * Numbers commit on blur or Enter (clamped), so typing "70" never passes
 * through a clamped "7".
 */
import React, { useEffect, useState } from 'react';
import { useWickRejectionSettingsStore } from '../store/wickRejectionSettingsStore';
import { useLevelsStore } from '../store/levelsStore';
import {
  WICK_ATR_RANGE,
  WICK_BROKEN_MODES,
  WICK_CONFIRM_RANGE,
  WICK_EMOJIS,
  WICK_PCT_RANGE,
  type WickBrokenMode,
  type WickSettings,
} from '../utils/wickRejection';

const RULE_HINT =
  'Closed bars only. The longer wick >= X% of the bar range and >= Y x ATR(14), close in the other half of the range. Faint until a later bar closes past the body within N bars; if the wick tip trades first it is broken: drawn very small and fainter, or hidden (Broken hooks). Strong (larger, amber rim) when the wick swept HOD/LOD, PDH/PDL, ONH/ONL or VWAP and the body held, a 🐋/🦈 spike hit the bar, or CME delta leaned into the wick. Display only, no alerts.';

type NumKey = 'minWickPct' | 'minWickAtr' | 'confirmBars';

function useNumberDraft(key: NumKey) {
  const value = useWickRejectionSettingsStore((s) => s[key]);
  const setSettings = useWickRejectionSettingsStore((s) => s.setSettings);
  const [draft, setDraft] = useState(String(value));
  useEffect(() => setDraft(String(value)), [value]);
  const commit = () => {
    const v = Number(draft);
    if (Number.isFinite(v) && draft.trim() !== '') setSettings({ [key]: v } as Partial<WickSettings>);
    setDraft(String(useWickRejectionSettingsStore.getState()[key]));
  };
  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') commit();
  };
  return { value, draft, setDraft, commit, onKeyDown };
}

const FIELDS: { key: NumKey; label: string; unit: string; range: { min: number; max: number }; step: number; aria: string }[] = [
  { key: 'minWickPct', label: 'Wick ≥', unit: '% of range', range: WICK_PCT_RANGE, step: 5, aria: 'Minimum wick percent of range' },
  { key: 'minWickAtr', label: 'Wick ≥', unit: 'x ATR', range: WICK_ATR_RANGE, step: 0.05, aria: 'Minimum wick in ATRs' },
  { key: 'confirmBars', label: 'Confirm within', unit: 'bars', range: WICK_CONFIRM_RANGE, step: 1, aria: 'Bars to confirm' },
];

const NumberInput: React.FC<{ field: (typeof FIELDS)[number]; testId: string; className?: string; style?: React.CSSProperties; disabled?: boolean }> = ({
  field,
  testId,
  className,
  style,
  disabled,
}) => {
  const { draft, setDraft, commit, onKeyDown } = useNumberDraft(field.key);
  return (
    <input
      type="number"
      className={className}
      min={field.range.min}
      max={field.range.max}
      step={field.step}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={onKeyDown}
      aria-label={field.aria}
      data-testid={testId}
      style={style}
      disabled={disabled}
    />
  );
};

const EmojiSelect: React.FC<{ testId: string; className?: string; style?: React.CSSProperties; disabled?: boolean }> = ({ testId, className, style, disabled }) => {
  const emoji = useWickRejectionSettingsStore((s) => s.emoji);
  const setSettings = useWickRejectionSettingsStore((s) => s.setSettings);
  return (
    <select
      className={className}
      value={emoji}
      onChange={(e) => setSettings({ emoji: e.target.value })}
      aria-label="Wick rejection emoji"
      data-testid={testId}
      style={style}
      disabled={disabled}
    >
      {WICK_EMOJIS.map((e) => (
        <option key={e} value={e}>
          {e}
        </option>
      ))}
    </select>
  );
};

const RequireLevel: React.FC<{ testId: string; className?: string; disabled?: boolean }> = ({ testId, className, disabled }) => {
  const requireLevel = useWickRejectionSettingsStore((s) => s.requireLevel);
  const setSettings = useWickRejectionSettingsStore((s) => s.setSettings);
  return (
    <input
      type="checkbox"
      className={className}
      checked={requireLevel}
      onChange={(e) => setSettings({ requireLevel: e.target.checked })}
      aria-label="Only wicks that swept a level"
      data-testid={testId}
      disabled={disabled}
    />
  );
};

const BROKEN_LABEL: Record<WickBrokenMode, string> = { faded: 'show small & faded', hide: 'hide' };

const BrokenHooksSelect: React.FC<{ testId: string; className?: string; style?: React.CSSProperties; disabled?: boolean }> = ({ testId, className, style, disabled }) => {
  const brokenHooks = useWickRejectionSettingsStore((s) => s.brokenHooks);
  const setSettings = useWickRejectionSettingsStore((s) => s.setSettings);
  return (
    <select
      className={className}
      value={brokenHooks}
      onChange={(e) => setSettings({ brokenHooks: e.target.value as WickBrokenMode })}
      aria-label="Broken hooks"
      data-testid={testId}
      style={style}
      disabled={disabled}
    >
      {WICK_BROKEN_MODES.map((m) => (
        <option key={m} value={m}>
          {BROKEN_LABEL[m]}
        </option>
      ))}
    </select>
  );
};

const inputStyle: React.CSSProperties = { width: 56, fontSize: 11, padding: '1px 4px' };

export const WickRejectionSettingsRow: React.FC = () => (
  <div className="levels-vp-row" style={{ padding: '2px 12px 6px 32px', gap: 10, flexWrap: 'wrap' }} data-testid="wick-settings" title={RULE_HINT}>
    <label className="levels-vp-label" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      Emoji
      <EmojiSelect testId="wick-emoji" style={{ fontSize: 12 }} />
    </label>
    {FIELDS.map((f) => (
      <label key={f.key} className="levels-vp-label" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        {f.label}
        <NumberInput field={f} testId={`wick-${f.key}`} style={inputStyle} />
        {f.unit}
      </label>
    ))}
    <label className="levels-vp-label" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      <RequireLevel testId="wick-requireLevel" />
      Only level sweeps
    </label>
    <label className="levels-vp-label" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      Broken hooks
      <BrokenHooksSelect testId="wick-brokenHooks" style={{ fontSize: 11 }} />
    </label>
  </div>
);

export const WickRejectionSettingsPanel: React.FC = () => {
  const enabled = useLevelsStore((s) => !!s.visibility.wickRejection);
  const setLevelVisible = useLevelsStore((s) => s.setLevelVisible);
  const emoji = useWickRejectionSettingsStore((s) => s.emoji);
  return (
    <div className="gs-tab-content" data-testid="wick-panel">
      <div className="gs-section">
        <div className="gs-section-header">
          <span className="gs-section-icon">{emoji}</span>
          <span className="gs-section-title">Wick rejection (possible reversal)</span>
        </div>
        <div className="gs-row">
          <div className="gs-label">
            <span className="gs-label-main">Show {emoji} on candles</span>
            <span className="gs-label-hint">Every symbol. Also in the Levels menu (Markers); Hide All and Clean (Signal markers) cover it. {RULE_HINT}</span>
          </div>
          <input
            type="checkbox"
            className="gs-checkbox"
            checked={enabled}
            onChange={(e) => setLevelVisible('wickRejection', e.target.checked)}
            aria-label="Show wick rejection markers"
            data-testid="wick-enabled"
          />
        </div>
        <div className="gs-row">
          <div className="gs-label">
            <span className="gs-label-main">Emoji</span>
            <span className="gs-label-hint">Drawn at the wick tip: under the low for a lower wick, over the high for an upper wick.</span>
          </div>
          <EmojiSelect testId="wick-panel-emoji" className="gs-input" disabled={!enabled} />
        </div>
        {FIELDS.map((f) => (
          <div className="gs-row" key={f.key}>
            <div className="gs-label">
              <span className="gs-label-main">
                {f.label} ({f.unit})
              </span>
              <span className="gs-label-hint">
                Range {f.range.min}-{f.range.max}.
              </span>
            </div>
            <NumberInput field={f} testId={`wick-panel-${f.key}`} className="gs-input" disabled={!enabled} />
          </div>
        ))}
        <div className="gs-row">
          <div className="gs-label">
            <span className="gs-label-main">Only level sweeps</span>
            <span className="gs-label-hint">Hide every mark whose wick did not sweep HOD/LOD, PDH/PDL, ONH/ONL or VWAP and close back.</span>
          </div>
          <RequireLevel testId="wick-panel-requireLevel" className="gs-checkbox" disabled={!enabled} />
        </div>
        <div className="gs-row">
          <div className="gs-label">
            <span className="gs-label-main">Broken hooks</span>
            <span className="gs-label-hint">A hook whose wick tip traded before a confirming close. Show it very small and faded at the tip (default), or hide it.</span>
          </div>
          <BrokenHooksSelect testId="wick-panel-brokenHooks" className="gs-input" disabled={!enabled} />
        </div>
        <div className="gs-row">
          <div className="gs-label">
            <span className="gs-label-hint">Changes apply immediately and are saved in this browser.</span>
          </div>
        </div>
      </div>
    </div>
  );
};
